// @ts-nocheck
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { colors, fontSizes, layout, fontFamily, shadows } from '@/theme';
import Fetch from '@/utils/Fetch';
import ButtonDropdown from '@/components/button-dropdown';
import DownloadService from '@/utils/download-service';
import Helper from '@/utils/helper';
import * as Enums from '@/utils/enums';
import JobDetail from '@/components/customer-zone/job/job-detail';
import JobStatusChange from '@/components/customer-zone/job/job-status-change';
import QuoteDetail from '@/components/customer-zone/quote/quote-detail';
import MessagePost from '@/components/modals/message-post';
import Router, { useRouter } from 'next/router';
import Button from '@/components/button';
import ConfirmAction from '@/components/modals/confirm-action';
import ToastContext from "@/utils/toast-context";
import PageContext from '@/utils/page-context';
import NoSSR from '@/utils/no-ssr'
import useMobileView from '@/hooks/useMobileView';
import JobRatings from '@/components/customer-zone/job/rating/job-ratings';
import LeadMessage from '@/components/customer-zone/general/lead-message';
import CompanyService from '@/services/company-service';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router'
import { LoadingOverlay, Flex, Text, Loader, Button as MantineButton } from '@mantine/core';
import { IconBrandWhatsapp } from '@tabler/icons-react';

function ViewJob() {

  const toastContext = useContext(ToastContext);
  const pageContext = useContext(PageContext);
  const [mobileView] = useMobileView();

  const router = useRouter();
  const { t, c, i, dt } = router.query;
 // TODO(tanstack-router): verify router.query → useParams/useSearch

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0 ? tenantData.Results[0] : null;

  const { data: documentData } = useQuery(
    ['czJobDocument', t, c],
    () => Fetch.get({
      url: `/CustomerZone/Document`,
      params: {},
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    }),
    { enabled: !!tenant }
  );

  const { data: jobData } = useQuery(
    ['czJob', t, c, i],
    () => Fetch.get({
      url: `/CustomerZone/GetJob`,
      params: { jobCardID: i },
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    }),
    { enabled: !!tenant }
  );

  const { data: quotesData } = useQuery(
    ['czJobQuotes', t, c, i],
    () => {
      const quoteStatuses = [
        Enums.getEnumStringValue(Enums.QuoteStatus, Enums.QuoteStatus.Approved),
        Enums.getEnumStringValue(Enums.QuoteStatus, Enums.QuoteStatus.Accepted),
        Enums.getEnumStringValue(Enums.QuoteStatus, Enums.QuoteStatus.Invoiced),
        Enums.getEnumStringValue(Enums.QuoteStatus, Enums.QuoteStatus.Declined),
      ];
      return Fetch.post({
        url: '/CustomerZone/GetQuotes',
        params: {
          pageSize: 10,
          IncludeClosed: true,
          ItemId: i,
          PopulatedList: false,
          QuoteStatusIDList: quoteStatuses
        },
        tenantID: t,
        customerID: c,
        apiUrlOverride: tenant.API
      });
    },
    { enabled: !!tenant }
  );

  const { data: statusChangesData } = useQuery(
    ['czJobStatusChanges', t, c, i],
    () => Fetch.get({
      url: `/CustomerZone/GetJobStatusChanges`,
      params: { jobCardID: i },
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    }),
    { enabled: !!tenant }
  );

  const companyQuery = useQuery(
    ['czCompany', t, c],
    () => CompanyService.getCustomerZoneCompany(t, c, tenant.API),
    { enabled: !!tenant, onError: console.error }
  );

  const company = {...companyQuery.data};

  const [disableAcceptReject, setDisableAcceptReject] = useState(false);
  const [job, setJob] = useState<any>(null);
  const [quotes, setQuotes] = useState<any>(null);
  const [jobStatusChanges, setJobStatusChanges] = useState<any>(null);
  const [legacyDocuments, setLegacyDocuments] = useState<any>(null);
  const [documentDefinition, setDocumentDefinition] = useState<any>(null);
  const [autoDownloaded, setAutoDownloaded] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (tenantData && !tenant) {
      router.replace(`/customerzone/joblist?t=${t}&c=${c}`);
    }
  }, [tenantData]);

  useEffect(() => {
    if (jobData && !job) {
      setJob(jobData);
      (pageContext as any).updateTenantName(jobData.headerTenantName);
    }
  }, [jobData]);

  useEffect(() => {
    if (quotesData && !quotes) {
      setQuotes(quotesData.Results);
    }
  }, [quotesData]);

  useEffect(() => {
    if (statusChangesData && !jobStatusChanges) {
      setJobStatusChanges(statusChangesData.Results);
    }
  }, [statusChangesData]);

  useEffect(() => {
    if (documentData && legacyDocuments === null) {
      setLegacyDocuments(documentData.legacyDocuments);
      setDocumentDefinition(documentData.documentDefinition);
    }
  }, [documentData]);

  useEffect(() => {
    if (dt && job && tenant && legacyDocuments !== null && !autoDownloaded) {
      if (dt === 'legacy') {
        exportLegacyDocument();
      } else {
        exportDocument(dt);
      }
      setAutoDownloaded(true);
    }
  }, [dt, job, tenant, legacyDocuments, autoDownloaded]);

  const applicableDocuments = useMemo(() => {
    if (legacyDocuments === null || legacyDocuments === undefined) return [];
    if (legacyDocuments) return [];
    if (!documentDefinition) return [];

    let meta = JSON.parse(documentDefinition.MetaData);
    let jobDocuments = meta.JobDocuments;
    return jobDocuments.filter((x: any) => x.CustomerZonePrintable && x.IsActive).map((x: any) => ({
      link: x.DocumentType,
      text: x.Title
    }));
  }, [legacyDocuments, documentDefinition]);

  const exportLegacyDocument = () => {
    setDownloading(true);
    DownloadService.downloadFile("GET", `/CustomerZone/GetJobCustomerDocument?jobCardID=${job.ID}`, null, true, false,
      (t as any), (c as any), tenant.API, mobileView, () => setDownloading(false));
  };

  const exportDocument = (documentType: any) => {
    setDownloading(true);
    DownloadService.downloadFile("GET", `/CustomerZone/GetJobDocument?jobCardID=${job.ID}&documentType=${documentType}`, null, true, false,
      (t as any), (c as any), tenant.API, mobileView, () => setDownloading(false));
  };

  const handleWhatsAppClick = () => {
    if (!company?.WhatsAppNumber) return;

    const message = `Hi! This is ${job.CustomerName} reaching out about job ${job.JobCardNumber}.`;
    let phone = company.WhatsAppNumber.replace(/\D/g, '');
    if (phone.startsWith('0')) {
      phone = '27' + phone.substring(1);
    }

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const [showMessagePost, setShowMessagePost] = useState(false);
  const onMessagePost = (state: any) => {
    setShowMessagePost(false);
  };

  const [confirmOptions, setConfirmOptions] = useState({ display: false });

  const acceptRejectPrompt = (accept: any) => {
    setConfirmOptions({
      display: true,
      heading: `${(accept ? "Accept" : "Decline")} Quote?`,
      cancelButtonText: "Cancel",
      confirmButtonText: `${(accept ? "Accept" : "Decline")} Quote`,
      showCancel: true,
      showConfirm: true,
      text: "This will update the status of the quote if you continue.",
      onConfirm: () => {
        doAcceptReject(accept);
      }
    });
  };

  const doAcceptReject = async (accept: any) => {
    setDisableAcceptReject(true);

    const jobResult = await Fetch.get({
      url: `/CustomerZone/JobAcceptReject`,
      params: { jobCardID: job.ID, accept },
      toastCtx: toastContext,
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    });

    setJob(jobResult);
    setDisableAcceptReject(false);
  };

  const displayJobQuoteButtons = () => {
    return (<>
      {job.ShowAcceptReject ?
        <div className="quote-buttons">
          <div className={mobileView ? "row" : ""}>
            <div className={mobileView ? "left" : "btn-container"}>
              <Button disabled={disableAcceptReject} text="Decline" extraClasses="red-light small-margin" onClick={() => acceptRejectPrompt(false)} />
            </div>
            <div className={mobileView ? "right" : "btn-container"}>
              <Button disabled={disableAcceptReject} text="Accept" extraClasses="green small-margin" onClick={() => acceptRejectPrompt(true)} />
            </div>
          </div>
        </div> : ""}

      <style jsx>{`
       .quote-buttons {
        margin-bottom: 0.5rem;
        ${mobileView ? "width: 100%;" : ""}
      }

      .btn-container {
        display: inline-block;
        margin-left: 0.5rem;
      }

      .row {
        display: flex;
        justify-content: space-between;
        width: 100%;
      }
      .column {
        display: flex;
        flex-basis: 0;
        flex-direction: column;
        flex-grow: 1;
      }

      .left {
        position: relative;
        margin-left: 0.5rem;
        width: calc(50vw - 0.75rem);
      }
      .right {
        position: relative;
        margin-right: 0.5rem;
        width: calc(50vw - 0.75rem);
      }
      `}</style>
    </>);
  };

  function signClick(e) {
    const job = jobData;
    window.open(`/customerzone/signature?t=${t}&c=${c}&i=${job.ID}`, "_blank");
  }

  if (!job || !tenant || quotes === null || jobStatusChanges === null || legacyDocuments === null) return null;

  const primaryContact = job.Contact ? job.Contact : undefined;
  const jobLocation = job.Location ? job.Location : undefined;

  return (
    <NoSSR>
      <LeadMessage customerID={c} tenantID={t} page={"customerzone/viewjob"} />

      <div
          style={{
            paddingInlineEnd: 'var(--mantine-spacing-sm)',
            minHeight: 'calc(100dvh - 20px)',
            position: 'relative',
            ...(downloading && { height: '100dvh', overflow: 'hidden' })
          }}
      >
        <LoadingOverlay
            visible={downloading}
            zIndex={1000}
            overlayProps={{ radius: 'sm', blur: 2 }}
            loaderProps={{children: <>
                <Flex style={{zIndex: 1000}} h={'90vh'} pt={'20vh'} direction="column" align="center" justify={'start'} gap="sm">
                  <Loader size="lg" />
                  <Text fw={500}>Preparing document download</Text>
                </Flex> </>}}
        />
        <Flex>
          <Flex>
            <h3>{job.JobCardNumber}</h3>
          </Flex>
          <Flex ml={'auto'}>
            <Flex ml={'auto'} wrap={'wrap-reverse'}>

              <Flex ml={'auto'}>
                {!mobileView ? displayJobQuoteButtons() : ""}
              </Flex>

              <Flex ml={'auto'}>
                {company?.WhatsAppNumber && (
                    <MantineButton
                        bg={'white'}
                        variant="outline"
                        color="#25D366"
                        leftSection={<IconBrandWhatsapp size={16} />}
                        onClick={handleWhatsAppClick}
                        name="wa-cz-job-followup"
                        h={40}
                        mt={8}
                        mr={8}
                        styles={{
                          root: {
                            fontWeight: 500,
                            flexShrink: 0
                          }
                        }}
                    >
                      Follow up on WhatsApp
                    </MantineButton>
                )}
              </Flex>

              <Flex ml={'auto'}>
                <Button
                    onClick={signClick}
                    text="Sign"
                    extraClasses="small-margin"
                />
                <div title="Submit question or comment" className="message-button-container" onClick={() => setShowMessagePost(true)}>
                  <img src={Enums.Icon.MessageCircle} alt="icon" className="icon" />
                </div>
                {showMessagePost ?
                    <MessagePost
                        onMessagePost={onMessagePost}
                        module={Enums.Module.JobCard}
                        itemID={job.ID}
                        customerZone={true}
                        tenantID={t}
                        customerID={c}
                        apiUrlOverride={tenant.API}
                    /> : ''
                }
                <div className="button-dropdown">
                  {legacyDocuments ?
                      <>
                        <Button
                            onClick={exportLegacyDocument}
                            text="Print"
                            extraClasses="small-margin"
                        />
                      </>
                      : <div style={{marginTop: "0.5rem"}}>
                        <ButtonDropdown
                            extraClasses="wide-print"
                            title={applicableDocuments.length === 0 ? "Documents not available" : ""}
                            disabled={applicableDocuments.length === 0}
                            text="Print"
                            action={(link: any) => exportDocument(link)}
                            options={applicableDocuments}
                        />
                      </div>}
                </div>
              </Flex>

            </Flex>
          </Flex>
        </Flex>

        <div className="row">
          <div className="contact">
            {primaryContact ?
                <>
                  <p className="contact-name">{primaryContact.FirstName + " " + primaryContact.LastName}</p>
                  <p>{job.CustomerName}</p>
                  <p>{primaryContact.MobileNumber}</p>
                  <p>{primaryContact.EmailAddress}</p>
                </>
                : ""}
            <p>{jobLocation ? jobLocation.LocationDisplay : "No Location"}</p>
          </div>
        </div>
        <br />
        <div className="row">
          <JobDetail job={job} customerID={c} tenantID={t} tenant={tenant} mobileView={mobileView} />
        </div>
        <div className="row margin-top">
          <JobStatusChange jobStatusChanges={jobStatusChanges} />
        </div>
        <div className='row margin-top'>
          <JobRatings jobCardID={job.ID} customerID={c} tenantID={t} api={tenant.API} />
        </div>
        {quotes && quotes.length > 0 ? <>
          <div className="row">
            <h2 className="item-heading">Quotes</h2>
          </div>

          {quotes.map(function (quote: any) {
            return (<>
              <br />
              <QuoteDetail quote={quote} showPrint={true} tenantID={t} customerID={c}
                           showTotals={true} showHyperlink={true} apiUrlOverride={tenant.API} mobileView={mobileView} />

            </>);
          })}

        </> : <></>}
        {mobileView && job.ShowAcceptReject ? <div className="row spacer">
          <div className="floating-action-button row">
            {displayJobQuoteButtons()}
          </div>
        </div> : ""}
      </div>

      <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />

      <style jsx>{`
      
        .item-heading {
          margin: 1rem 0 0 0;
          padding: 0;
          font-size: 1.1rem;
        }

        .margin-top {
          margin-top: 0.5rem;
        }
        .card {
            background-color: ${colors.white};
            border-radius: ${layout.cardRadius};
            box-shadow: ${shadows.card};
            box-sizing: border-box;
            padding: 1rem 2rem;
            position: relative;
            width: 100%;
        }

        .card h3 {
            margin-top: 0;
        }

        .spacer {
          height: 4rem;
        }

        .container {
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            padding: 1.5rem 3rem;
            overflow-x: hidden;
          }
          .row {
            display: flex;
          }
          .column {
            display: flex;
            flex-basis: 0;
            flex-direction: column;
            flex-grow: 1;
          }
          .column-end {
            align-items: flex-end;
          }
          .column :global(.textarea-container) {
            height: 100%;
          }
          .column + .column {
            margin-left: 1.25rem;
          }
          .contact {
            width: 100%;
            padding-right: 16px;
          }

          .contact p {
            margin: 3px 0 0;
          }
          
          .contact p.contact-name {
            font-weight: bold;
          }
          .heading {
            color: ${colors.blueGrey};
            font-weight: bold;
          }
          .new-comment {
            position: relative;
          }
          .new-comment :global(.textarea-container){
            height: 5rem;
          }
          .new-comment img{
            cursor: pointer;
            position: absolute;
            right: 1rem;
            top: 1rem;
          }
          .loader {
            border-color: rgba(113, 143, 162, 0.2);
            border-left-color: ${colors.blueGrey};
            display: block;
            margin-bottom: 1rem;
            margin-top: 1rem;
          }
  
          .comment {
            background-color: ${colors.white};
            border-radius: ${layout.cardRadius};
            box-sizing: border-box;
            color: ${colors.blueGrey};
            display: flex;
            flex-direction: column;
            justify-content: center;
            margin-top: 0.5rem;
            padding: 1.25rem 1rem;
            position: relative;
            width: 100%;
          }
          .comment-info {
            align-items: center;
            display: flex;
            margin-bottom: 4px;
          }
          .job {
            color: ${colors.bluePrimary};
            font-weight: bold;
          }
          .name {
            color: ${colors.darkPrimary};
            font-weight: bold;
          }
          .time {
            color: ${colors.blueGrey};
            font-size: 12px;
            margin-left: 1rem;
          }
          .text {
            white-space: pre-wrap;
          }
          .edit {
            margin-top: 0.2rem;
            margin-left: 1rem;
          }
          .status {
            position: absolute;
            right: 0;
            top: 0;
            width: max-content;
          }
          .status :global(.input-container){
            background-color: ${colors.bluePrimary};
          }
          .status :global(input){
            color: ${colors.white};
          }
          .status :global(label){
            color: ${colors.white};
            opacity: 0.8;
          }
          .actions {
            display: flex;
            flex-direction: row;
          }
          .message-button-container {
            align-items: center;
            border: 1px solid ${colors.blueGreyLight};
            border-radius: ${layout.cardRadius};
            box-sizing: border-box;
            cursor: pointer;
            display: flex;
            font-weight: bold;
            height: 2.5rem;
            justify-content: space-between;
            margin-top: 0.5rem;
            padding: 0.5rem 0.5rem 0.5rem 0.5rem;
            position: relative;
            margin-right: 0.5rem;
            margin-left: 0.5rem;
            background: ${colors.white};
          }
          

          .accept-reject {
            margin: -1rem 2rem;
            display: flex;
            justify-content: space-between;
            width: 11rem;
          }

          .floating-action-button {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background: ${colors.white};
            box-shadow: ${shadows.cardSmallDark};
           
          }
       
      `}</style>
    </NoSSR>
  );
}

export default ViewJob;

export const Route = createFileRoute('/customerzone/viewjob')({ component: ViewJob })
